import styled, { keyframes } from 'styled-components'
import { theme } from '@provi/provi-components'

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`

const getRowBorderColor = $variant => ($variant === 'awaiting' ? '#E3E8F6' : '#E5E7EB')

export const Card = styled.div`
  background: white;
  border-radius: ${({ $variant }) => ($variant === 'awaiting' || $variant === 'cash' ? '16px' : '12px')};
  padding: ${({ $variant }) => ($variant === 'cash' ? '24px 20px' : $variant === 'awaiting' ? '20px' : '24px')};
  border: ${({ $variant }) => ($variant === 'awaiting' ? '1px solid #E3E8F6' : 'none')};
  box-shadow: ${({ $variant }) =>
    $variant === 'awaiting' ? '0px 12px 32px rgba(15, 23, 42, 0.12)' : '0 1px 3px rgba(0, 0, 0, 0.1)'};
  position: sticky;
  top: 24px;

  ${theme.breakpoints.down('md')} {
    position: static;
    border-radius: 0;
    order: -1;
    box-shadow: none;
    padding: 16px;
    margin-left: -24px;
    margin-right: -16px;
    width: calc(100% + 8px);
  }
`

export const Header = styled.div`
  margin-bottom: 20px;

  ${theme.breakpoints.down('md')} {
    margin-bottom: 16px;
  }
`

export const Title = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: #999999;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;

  ${theme.breakpoints.down('md')} {
    height: 28px;
  }
`

export const DiscountBadge = styled.div`
  background: #14b8aa;
  color: white;
  padding: 12px 0;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`

export const Row = styled.div`
  padding-left: ${({ $variant, $bordered }) => ($variant === 'cash' && !$bordered ? '16px' : '16px')};
  padding-right: ${({ $variant, $bordered }) => ($variant === 'cash' && !$bordered ? '16px' : '16px')};
  padding-top: ${({ $variant, $bordered }) => ($variant === 'cash' && $bordered ? '16px' : '16px')};
  padding-bottom: ${({ $variant, $bordered, $isLast }) => {
    if ($variant === 'cash' && !$bordered && $isLast) return '0'
    if ($variant === 'cash' && !$bordered) return '8px'
    return '16px'
  }};
  display: flex;
  flex-direction: column;
  border-left: ${({ $variant, $bordered }) => {
    if ($variant === 'cash' && $bordered) return '3px solid #E6E6E6'
    return `1px solid ${getRowBorderColor($variant)}`
  }};
  border-right: ${({ $variant, $bordered }) => {
    if ($variant === 'cash' && $bordered) return '3px solid #E6E6E6'
    return `1px solid ${getRowBorderColor($variant)}`
  }};
  border-bottom: ${({ $isLast, $variant, $bordered }) => {
    if ($variant === 'cash' && $bordered) return '3px solid #E6E6E6'
    if ($variant === 'cash' && !$bordered) return 'none'
    return $isLast ? 'none' : `1px solid ${getRowBorderColor($variant)}`
  }};
  border-top: ${({ $isFirst, $variant, $bordered }) => {
    if ($variant === 'cash' && $bordered && $isFirst) return '3px solid #E6E6E6'
    if ($variant === 'cash') return 'none'
    return $isFirst ? `1px solid ${getRowBorderColor($variant)}` : 'none'
  }};
  border-bottom-left-radius: ${({ $variant, $isLast, $bordered }) => {
    if ($variant === 'cash' && $bordered) return '8px'
    return $variant === 'awaiting' && $isLast ? '12px' : '0'
  }};
  border-bottom-right-radius: ${({ $variant, $isLast, $bordered }) => {
    if ($variant === 'cash' && $bordered) return '8px'
    return $variant === 'awaiting' && $isLast ? '12px' : '0'
  }};
  border-top-left-radius: ${({ $variant, $isFirst, $bordered }) => {
    if ($variant === 'cash' && $bordered) return '0'
    return $variant === 'awaiting' && $isFirst ? '12px' : '0'
  }};
  border-top-right-radius: ${({ $variant, $isFirst, $bordered }) => {
    if ($variant === 'cash' && $bordered) return '0'
    return $variant === 'awaiting' && $isFirst ? '12px' : '0'
  }};
  background: ${({ $variant }) => ($variant === 'awaiting' ? '#FFFFFF' : 'transparent')};
  margin-top: ${({ $variant, $bordered }) => ($variant === 'cash' && !$bordered ? '8px' : '0')};

  ${theme.breakpoints.down('md')} {
    border-left: ${({ $variant, $bordered }) => {
      if ($variant === 'cash' && $bordered) return '3px solid #E6E6E6'
      return `1px solid ${getRowBorderColor($variant)}`
    }};
    border-right: ${({ $variant, $bordered }) => {
      if ($variant === 'cash' && $bordered) return '3px solid #E6E6E6'
      return `1px solid ${getRowBorderColor($variant)}`
    }};
    border-bottom: ${({ $isLast, $variant, $bordered }) => {
      if ($variant === 'cash' && $bordered) return '3px solid #E6E6E6'
      if ($variant === 'cash' && !$bordered) return 'none'
      return $isLast ? 'none' : `1px solid ${getRowBorderColor($variant)}`
    }};
    border-top: ${({ $isFirst, $variant, $bordered }) => {
      if ($variant === 'cash' && $bordered && $isFirst) return '3px solid #E6E6E6'
      if ($variant === 'cash') return 'none'
      return $isFirst ? `1px solid ${getRowBorderColor($variant)}` : 'none'
    }};
  }
`

export const InnerRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: ${({ alignTop }) => (alignTop ? 'flex-start' : 'center')};
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  ${theme.breakpoints.down('md')} {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

export const Label = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: ${({ gray, dark }) => {
    if (gray) return '#999999'
    if (dark) return '#262626'
    return '#6B7280'
  }};
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const Value = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: ${({ large }) => (large ? '16px' : '16px')};
  font-weight: ${({ large }) => (large ? '700' : '400')};
  line-height: 24px;
  color: ${({ gray, dark }) => {
    if (gray) return '#999999'
    if (dark) return '#262626'
    return '#1F2937'
  }};
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

export const OldValue = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #999999;
  text-decoration: line-through;
  margin-top: 4px;
  font-weight: 400;
`

export const Link = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: ${({ $disabled }) => ($disabled ? '#94A3B8' : '#0273FF')};
  text-decoration: ${({ $disabled }) => ($disabled ? 'none' : 'underline')};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  display: inline-block;
  margin-top: 4px;
  background: transparent;
  border: none;
  padding: 0;
  text-align: left;
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  &:hover {
    color: ${({ $disabled }) => ($disabled ? '#94A3B8' : '#0052CC')};
  }
`

export const Placeholder = styled.div`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '14px'};
  border-radius: 999px;
  background: linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 50%, #e5e7eb 100%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`
